import "@fontsource/material-icons";
import "@fontsource/material-symbols-outlined";
import "@lmax-exchange/lmax-common-ui/styles.css";
import * as Sentry from "@sentry/vue";
import type { Plugin } from "vue";
import { handleEnvVars } from "@/config/apiClient.config";
import { i18n } from "@/config/locales";
import { auth0Instance } from "@/services/authService";
import { errorMessageOf, isChunkLoadError } from "@/services/chunkLoadError";

const { mockMode, requiresAuth0 } = handleEnvVars(import.meta.env);

// Set only when this listener starts a reload. Most chunk-load failures are
// promise rejections, which never reach it, so beforeSend mustn't assume one.
let isReloadingForChunkError = false;

window.addEventListener("error", (event) => {
  const errorMessage = event.message || event.error?.message || "";

  if (isChunkLoadError(errorMessage)) {
    const hasReloaded = sessionStorage.getItem("chunk-load-reloaded");

    if (!hasReloaded) {
      console.warn(
        "Chunk load error detected. Reloading to fetch latest version...",
      );
      sessionStorage.setItem("chunk-load-reloaded", "true");
      isReloadingForChunkError = true;
      window.location.reload();
    }
  }
});

(async () => {
  if (mockMode) {
    console.warn(`Mock mode: ${mockMode}`);
    const { msw } = await import("./mocks/browser");
    await msw.start();
  }

  const { createApp } = await import("vue");
  const { createPinia } = await import("pinia");
  const { components: CommonUI } =
    await import("@lmax-exchange/lmax-common-ui");

  const App = (await import("./App.vue")).default;
  const router = (await import("./router.ts")).default;
  const { createHubRoutes, fetchHubApps } = await import("./hubApps");
  for (const route of createHubRoutes(await fetchHubApps())) {
    router.addRoute(route);
  }

  const app = createApp(App);
  const pinia = createPinia();

  app.use(pinia);
  app.use(router);
  app.use(i18n);

  // Initialize Sentry for error tracking and performance monitoring
  if (import.meta.env.VITE_SENTRY_DSN) {
    const environment =
      import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
    const isProduction = environment === "production";
    const isUAT = environment === "uat";

    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment,
      release: import.meta.env.VITE_SENTRY_RELEASE
        ? `${import.meta.env.VITE_SENTRY_RELEASE}`
        : `${environment}-${__GIT_SHA__}`,

      // Privacy: Don't send PII automatically (we'll set user context manually)
      sendDefaultPii: false,

      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          // Privacy: Mask text in production/uat for data protection
          maskAllText: isProduction || isUAT,
          blockAllMedia: isProduction || isUAT,
        }),
      ],

      // Performance Monitoring - lower sampling in production to reduce costs
      tracesSampleRate: isProduction ? 0.05 : isUAT ? 0.1 : 0.0,

      replaysSessionSampleRate: 0.0, // don't capture general session recording to limit the cost
      replaysOnErrorSampleRate: 1.0, // capture all errors

      beforeSend(event, hint) {
        const errorMessage = errorMessageOf(hint.originalException);

        // App load failures are caught by HubAppSlot, which shows an error
        // rather than reloading; reportAppMountFailure tags those itself.
        const isAppMountFailure = event.tags?.hub_app_phase === "mount";

        if (isChunkLoadError(errorMessage) && !isAppMountFailure) {
          const hasReloaded = sessionStorage.getItem("chunk-load-reloaded");
          const recovery: {
            autoRecovery: string;
            level: Sentry.SeverityLevel;
            key: string;
          } = isReloadingForChunkError
            ? {
                autoRecovery: "will_reload",
                level: "info",
                key: "auto-recovered",
              }
            : hasReloaded
              ? { autoRecovery: "failed", level: "error", key: "reload-failed" }
              : { autoRecovery: "none", level: "warning", key: "no-recovery" };

          event.tags = {
            ...event.tags,
            error_type: "chunk_load_error",
            auto_recovery: recovery.autoRecovery,
          };
          event.level = recovery.level;
          // Group these errors together in Sentry, one issue per outcome
          event.fingerprint = ["chunk-load-error", recovery.key];
        }

        return event;
      },
    });
  }

  // Casting to fix npm-link related type issues with CommonUI
  app.use(
    CommonUI as unknown as Plugin,
    undefined, // Custom primevue
    undefined, // custom theme preset
    {
      materialIcons: ["delete", "nest_clock_farsight_analog"],
      selfHostedMaterialIcons: true,
    },
  );

  if (requiresAuth0) {
    app.use(auth0Instance);
  }

  app.mount("#app");
})();
